// Mock implementation of the dashboard map endpoints.
//
// Mirrors the signatures in dashboardApi.js exactly — swapping in the real backend means
// changing dashboardApi.js only, never the map components.
//
// Positions are interpolated from timetable offsets with the same routine the train detail
// view uses (trainPosition.js). Delays, track changes and messages are derived from a hash of
// (operating day, train), so every operating day produces a different — but reproducible —
// picture.
//
// All thresholds and definitions follow railapi_daily_operations_report.py, not the concept
// document, so that dashboard and daily mail cannot disagree:
//   punctual        < 6 min
//   high delay      >= 60 min
//   operating day   04:00 → 04:00 next morning (Europe/Berlin)
//   min sample      3 trains per route/segment, below that no verdict

import { classifyTrainType } from './trainUtils';
import {
  interpolatePosition,
  trainGroupOf,
  DELAY_THRESHOLD_MIN,
  HIGH_DELAY_THRESHOLD_MIN,
  MIN_TRAINS_FOR_VERDICT,
  OPERATING_DAY_START_HOUR,
} from './trainPosition';

// Deterministic UUID from a seed string, so mock identifiers stay stable across reloads
const uuidFrom = (seedText) => {
  const rnd = mulberry32(hash32(seedText));
  const hex = (n) => Array.from({ length: n }, () => '0123456789abcdef'[Math.floor(rnd() * 16)]).join('');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-a${hex(3)}-${hex(12)}`;
};

// The mock dataset is ~2 MB — load it lazily so it never lands in the main bundle.
let dataPromise = null;
const loadData = () => {
  if (!dataPromise) {
    dataPromise = Promise.all([
      import('../data/mockupdata/map/mock-corridors.json'),
      import('../data/mockupdata/map/mock-trains.json'),
      import('../data/mockupdata/map/mock-kbs.json'),
      // Real Kursbuchstrecken from OpenStreetMap, if they have been fetched already
      import('../data/kbs/kbs-geometry.json').catch(() => null),
    ]).then(([corridorsMod, trainsMod, kbsMod, osmKbsMod]) => {
      const corridors = new Map(
        (corridorsMod.default || corridorsMod).corridors.map(c => [c.id, c])
      );
      const trains = (trainsMod.default || trainsMod).trains;
      const placeholderKbs = (kbsMod.default || kbsMod).kbs;
      const osmKbs = osmKbsMod ? (osmKbsMod.default || osmKbsMod).kbs : null;

      // Index of station name -> the stops that happen there, for the station drill-in
      const stationIndex = new Map();
      const stations = new Map();
      for (const corridor of corridors.values()) {
        corridor.stations.forEach(st => {
          if (!stations.has(st.name)) stations.set(st.name, { name: st.name, lat: st.lat, lng: st.lng });
        });
      }
      for (const train of trains) {
        const corridor = corridors.get(train.corridorId);
        if (!corridor) continue;
        train.stops.forEach((stop, idx) => {
          const name = corridor.stations[stop.s].name;
          let list = stationIndex.get(name);
          if (!list) { list = []; stationIndex.set(name, list); }
          list.push({ train, idx });
        });
      }

      // Stations need a stable identifier — the timetable board is keyed on it, exactly as
      // the real /v1/stations/{uuid}/timetable endpoint is.
      // uuid keys the timetable board, the numeric id keys the track geometry — the same
      // split the real API makes between UUID and globalStationID
      const stationList = Array.from(stations.values()).map((st, i) => ({
        ...st, uuid: uuidFrom(`station|${st.name}`), id: i + 1,
      }));

      return {
        corridors,
        trains,
        trainsByUuid: new Map(trains.map(t => [t.trainUUID, t])),
        kbs: placeholderKbs,
        osmKbs,
        stationIndex,
        stations: stationList,
        stationsByUuid: new Map(stationList.map(st => [st.uuid, st])),
        stationUuidByName: new Map(stationList.map(st => [st.name, st.uuid])),
        stationIdByName: new Map(stationList.map(st => [st.name, st.id])),
        stationsById: new Map(stationList.map(st => [st.id, st])),
      };
    });
  }
  return dataPromise;
};

// ---------------------------------------------------------------------------
// Deterministic per-day model
// ---------------------------------------------------------------------------
const hash32 = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const mulberry32 = (seed) => () => {
  seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

// Some days are simply worse than others (weather, construction, strikes)
const dayFactor = (opDay) => 0.55 + (hash32(`day|${opDay}`) % 1000) / 1000 * 1.25;
// Some routes are chronically weaker than others
const corridorFactor = (corridorId) => 0.6 + (hash32(`corr|${corridorId}`) % 1000) / 1000 * 1.1;

// Message texts modelled on the ones the daily report groups by
const MESSAGE_POOL = [
  { messageText: 'Verzögerungen im Betriebsablauf', priority: 'MITTEL' },
  { messageText: 'Bauarbeiten', priority: 'MITTEL' },
  { messageText: 'Verspätung eines vorausfahrenden Zuges', priority: 'MITTEL' },
  { messageText: 'Reparatur am Zug', priority: 'HOCH' },
  { messageText: 'Weichenstörung', priority: 'HOCH' },
  { messageText: 'Signalstörung', priority: 'HOCH' },
  { messageText: 'Ärztliche Versorgung eines Fahrgastes', priority: 'HOCH' },
  { messageText: 'Personen im Gleis', priority: 'HOCH' },
  { messageText: 'Warten auf einen Anschlusszug', priority: 'NIEDRIG' },
  { messageText: 'Kurzfristiger Personalausfall', priority: 'MITTEL' },
];

const profileCache = new Map();

// On-board features per product category, in the key set the detail view already knows
const ATTRIBUTE_POOLS = {
  longdistance: ['BF', 'BR', 'BK', 'KL', 'WR', 'HI', 'RZ'],
  localtrain: ['BF', 'KL', 'HI'],
  metro: ['KL', 'HI'],
  tram: ['HI'],
  bus: ['HI'],
};

/**
 * Per-train, per-day profile: delay at every stop, track and capacity, cancellation,
 * message and on-board features.
 */
const trainProfile = (train, opDay) => {
  const key = `${opDay}|${train.id}`;
  const cached = profileCache.get(key);
  if (cached) return cached;

  const rnd = mulberry32(hash32(key));
  const factor = dayFactor(opDay) * corridorFactor(train.corridorId);
  const cancelled = rnd() < 0.012;
  const longDistance = train.trainTypeProductCategorie === 'longdistance';

  const delays = [];
  const trackChanges = [];
  const tracks = [];
  const capacity = [];
  // Most trains start punctually, a few inherit a delay from their previous run
  let d = rnd() < 0.72 ? 0 : Math.round(rnd() * 8 * factor);
  for (let i = 0; i < train.stops.length; i++) {
    if (i > 0) {
      d += (rnd() - 0.52) * 3 * factor;
      if (rnd() < 0.04) d += rnd() * 25 * factor; // incident
      if (rnd() < 0.004) d += rnd() * 60 * factor; // major disruption → high delay
      d = Math.max(0, d);
    }
    delays.push(Math.round(d));

    const changed = rnd() < 0.035;
    trackChanges.push(changed);
    const scheduled = String(1 + Math.floor(rnd() * 14));
    tracks.push({
      scheduled,
      actual: changed ? String(1 + Math.floor(rnd() * 14)) : scheduled,
    });

    capacity.push(longDistance
      ? { c1: 1 + Math.floor(rnd() * 3), c2: 1 + Math.floor(rnd() * 4) }
      : { c1: 0, c2: 0 });
  }

  // Delayed trains carry an explanation, as in the report's message aggregation
  const worst = Math.max(...delays);
  const message = worst >= DELAY_THRESHOLD_MIN && rnd() < 0.75
    ? MESSAGE_POOL[Math.floor(rnd() * MESSAGE_POOL.length) % MESSAGE_POOL.length]
    : null;

  const pool = ATTRIBUTE_POOLS[train.trainTypeProductCategorie] || ATTRIBUTE_POOLS.localtrain;
  const attributes = pool.filter(() => rnd() < 0.7);

  const profile = { delays, trackChanges, tracks, capacity, cancelled, message, attributes };
  profileCache.set(key, profile);
  return profile;
};

// ---------------------------------------------------------------------------
// Operating day: 04:00 → 04:00 next morning, as in the report
// ---------------------------------------------------------------------------
const pad = (n) => String(n).padStart(2, '0');

const dateToKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const toOperatingDay = (date) => {
  const d = new Date(date.getTime());
  if (d.getHours() < OPERATING_DAY_START_HOUR) d.setDate(d.getDate() - 1);
  return dateToKey(d);
};

export const shiftOperatingDay = (opDay, days) => {
  const [y, m, d] = opDay.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  return dateToKey(date);
};

// Local-time ISO string without timezone suffix, matching the existing mockup JSON files
const minutesToLocalISO = (opDay, minutes) => {
  if (minutes === null || minutes === undefined) return null;
  const [y, m, d] = opDay.split('-').map(Number);
  const base = new Date(y, m - 1, d, 0, 0, 0, 0);
  base.setMinutes(base.getMinutes() + minutes);
  return `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}T` +
    `${pad(base.getHours())}:${pad(base.getMinutes())}:00`;
};

// Minutes since midnight of the operating day (can exceed 1440 after midnight)
const minutesOfDay = (date, opDay) => {
  const [y, m, d] = opDay.split('-').map(Number);
  const midnight = new Date(y, m - 1, d, 0, 0, 0, 0);
  return (date.getTime() - midnight.getTime()) / 60000;
};

const clockOf = (opDay, minutes) => {
  const iso = minutesToLocalISO(opDay, minutes);
  return iso ? iso.slice(11, 16) : '—';
};

// ---------------------------------------------------------------------------
const buildJourneyStations = (train, corridor, opDay, delays) =>
  train.stops.map((stop, i) => {
    const station = corridor.stations[stop.s];
    const delay = delays[i];
    return {
      stationName: station.name,
      lat: station.lat,
      lng: station.lng,
      kbs: stop.kbs,
      arrivalTime_scheduled: minutesToLocalISO(opDay, stop.arr),
      arrivalTime_actual: minutesToLocalISO(opDay, stop.arr === null ? null : stop.arr + delay),
      departureTime_scheduled: minutesToLocalISO(opDay, stop.dep),
      departureTime_actual: minutesToLocalISO(opDay, stop.dep === null ? null : stop.dep + delay),
    };
  });

const journeyWindow = (train) => ({
  start: train.stops[0].dep,
  end: train.stops[train.stops.length - 1].arr,
});

// Everything except the station filter, which needs the corridor to resolve names
const matchesFilters = (train, filters) => {
  if (!filters) return true;
  const { types, line, serie, kbs } = filters;
  if (types && types.length > 0 && !types.includes(classifyTrainType(train))) return false;
  if (line && train.trainLineName !== line) return false;
  if (serie && train.trainSerieNumber !== serie) return false;
  if (kbs && !train.stops.some(st => st.kbs === kbs)) return false;
  return true;
};

const touchesStation = (train, corridor, stationName) =>
  !stationName || train.stops.some(st => corridor.stations[st.s].name === stationName);

const inBbox = (lat, lng, bbox) => {
  if (!bbox) return true;
  const [minLng, minLat, maxLng, maxLat] = bbox;
  return lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng;
};

// ---------------------------------------------------------------------------
// Metric helpers — mirror per_group_metrics() from the report
// ---------------------------------------------------------------------------
const GROUPS = ['fern', 'nah', 'sbahn', 'sonstige', 'total'];

const emptyBuckets = () => {
  const b = {};
  GROUPS.forEach(g => { b[g] = { delays: [], trackChanges: 0 }; });
  return b;
};

const summarise = (buckets) => {
  const out = {};
  for (const g of GROUPS) {
    const delays = buckets[g].delays;
    const sorted = [...delays].sort((a, b) => a - b);
    const onTime = delays.filter(d => d < DELAY_THRESHOLD_MIN).length;
    out[g] = {
      count: delays.length,
      on_time_pct: delays.length > 0 ? (onTime / delays.length) * 100 : null,
      delay_median: sorted.length > 0 ? sorted[Math.floor(sorted.length / 2)] : 0,
      delay_avg: delays.length > 0 ? delays.reduce((s, d) => s + d, 0) / delays.length : 0,
      high_delay: delays.filter(d => d >= HIGH_DELAY_THRESHOLD_MIN).length,
      track_changes: buckets[g].trackChanges,
    };
  }
  return out;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Track geometry per station pair — mirrors GET /geo/segments.
 *
 * The real answer is cut from the course each run carries. The mock dataset has no course at
 * all, only station points, so there is nothing to cut: this bends the straight line into an
 * arc instead. It is **not** a real railway line and is not meant to look like one — its job
 * is to exercise the same code path as real geometry, so that walking a polyline is covered
 * by the mock-based tests rather than only in production.
 */
export async function fetchRouteSegments(pairs) {
  const { stationsById } = await loadData();

  const out = {};
  for (const key of pairs || []) {
    const [fromId, toId] = String(key).split('-').map(Number);
    const from = stationsById.get(fromId);
    const to = stationsById.get(toId);
    if (!from || !to) continue;

    // Deterministic side and depth of the bend, so a section looks the same on every load
    const seed = [...key].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 997, 7);
    const bend = ((seed % 2 ? 1 : -1) * (0.04 + (seed % 7) / 100));
    const midLat = (from.lat + to.lat) / 2 + (to.lng - from.lng) * bend;
    const midLng = (from.lng + to.lng) / 2 - (to.lat - from.lat) * bend;

    const steps = 14;
    const geometry = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const u = 1 - t;
      geometry.push([
        Number((u * u * from.lat + 2 * u * t * midLat + t * t * to.lat).toFixed(5)),
        Number((u * u * from.lng + 2 * u * t * midLng + t * t * to.lng).toFixed(5)),
      ]);
    }
    out[key] = { geometry, length_km: null };
  }
  return out;
}

/** Train positions at a point in time — mirrors GET /live/active */
export async function fetchActiveTrains({ at, bbox, filters } = {}) {
  const { corridors, trains, stationIdByName } = await loadData();
  const now = at instanceof Date ? at : new Date(at || Date.now());
  const opDay = toOperatingDay(now);
  const nowMin = minutesOfDay(now, opDay);

  const result = [];
  for (const train of trains) {
    const { start, end } = journeyWindow(train);
    // Only trains actually under way. interpolatePosition tolerates ±60 min around the
    // journey (useful when following a single train in the detail view), but on a fleet map
    // that would pile up hundreds of markers parked at terminal stations.
    if (nowMin < start || nowMin > end) continue;
    if (!matchesFilters(train, filters)) continue;

    const corridor = corridors.get(train.corridorId);
    if (!corridor) continue;
    if (!touchesStation(train, corridor, filters?.station)) continue;

    const { delays, cancelled } = trainProfile(train, opDay);
    const stations = buildJourneyStations(train, corridor, opDay, delays);
    const position = interpolatePosition(stations, [], now);
    if (!position) continue;

    const [lat, lng] = position.pos;
    if (!isFinite(lat) || !isFinite(lng)) continue;
    if (!inBbox(lat, lng, bbox)) continue;

    const toStationIdx = stations.indexOf(position.toStation);
    const currentDelay = delays[toStationIdx >= 0 ? toStationIdx : delays.length - 1];
    const atStation = position.progress <= 0.02 || position.progress >= 0.98;

    result.push({
      globalTrainID: train.trainUUID,
      // The real /live/active carries both; the run is looked up by UUID
      trainUUID: train.trainUUID,
      journeyUUID: train.journeyUUID,
      trainName: train.trainName_long,
      trainNumber: train.trainNumber,
      trainLineName: train.trainLineName,
      trainTypeProductCategorie: train.trainTypeProductCategorie,
      trainTypeValue: train.trainTypeValue,
      train_group: trainGroupOf(train),
      // The panel's header picture is keyed on this. Without it the mock would show no
      // photo for any train that has no journey, which is most of them - and the gap would
      // only turn up in production, exactly as the NULL delay figures did.
      trainSerieUUID: train.trainSerieUUID,
      // Both ends of the run. The API reads them out of the journeyID, so the panel can
      // name a destination for a train that has no journey - which is most of them.
      run: {
        origin: {
          name: corridor.stations[train.stops[0].s].name,
          time: minutesToLocalISO(opDay, train.stops[0].dep ?? train.stops[0].arr),
        },
        destination: {
          name: corridor.stations[train.stops[train.stops.length - 1].s].name,
          time: minutesToLocalISO(opDay, train.stops[train.stops.length - 1].arr),
        },
      },
      trainSerieNumber: train.trainSerieNumber,
      trainSerieName: train.trainSerieName,
      trainSerieOperator: train.trainSerieOperator,
      // The API resolves who runs this leg from the journey's HAFAS administration code and
      // falls back to the class. The mock network is domestic, so the two agree — but the
      // fields are emitted, because a mock that omits what the API sends hides exactly the
      // crashes it exists to catch.
      operator: train.trainSerieOperator,
      operator_country: 'DE',
      operator_source: train.trainSerieOperator ? 'series' : null,
      admin_id: null,
      lat,
      lng,
      bearing: position.bearing,
      current_delay: currentDelay,
      status: cancelled ? 'cancelled' : atStation ? 'at_station' : 'enroute',
      position_source: 'estimated',
      // The two anchors the client needs to keep the train moving between polls.
      // Without them it could only blend between snapshots and would trail a full interval.
      prev_station: position.fromStation ? {
        // Keys the track geometry, as globalStationID does on the real API
        id: stationIdByName.get(position.fromStation.stationName) || null,
        name: position.fromStation.stationName,
        lat: position.fromStation.lat,
        lng: position.fromStation.lng,
        time: position.fromStation.departureTime_actual ||
          position.fromStation.departureTime_scheduled,
      } : null,
      next_station: position.toStation ? {
        id: stationIdByName.get(position.toStation.stationName) || null,
        name: position.toStation.stationName,
        lat: position.toStation.lat,
        lng: position.toStation.lng,
        time: position.toStation.arrivalTime_actual ||
          position.toStation.arrivalTime_scheduled,
      } : null,
      terminus: train.trainTerminus,
      kbs_number: position.fromStation?.kbs || null,
      operatingDate: opDay,
      // mock-only handle so the selected train's route can be drawn
      _corridorId: train.corridorId,
      _reverse: train.reverse,
    });
  }
  return { trains: result, total: result.length, at: now, operatingDate: opDay };
}

// ---------------------------------------------------------------------------
// KBS aggregates — one full-day pass, cached per (day + filter signature)
// ---------------------------------------------------------------------------
const aggCache = new Map();

const filterSignature = (filters) => JSON.stringify({
  types: filters?.types || [],
  line: filters?.line || null,
  serie: filters?.serie || null,
  station: filters?.station || null,
});

async function aggregateDay(opDay, filters) {
  const key = `${opDay}|${filterSignature(filters)}`;
  if (aggCache.has(key)) return aggCache.get(key);

  const { corridors, trains, kbs, osmKbs } = await loadData();
  const names = new Map((osmKbs || kbs).map(k => [k.kbs_number, k.kbs_name]));
  const acc = new Map();

  const ensure = (number) => {
    if (!acc.has(number)) {
      acc.set(number, {
        kbs_number: number,
        kbs_name: names.get(number) || `KBS ${number}`,
        trainIds: new Set(),
        delays: [],
        cancelled_count: 0,
        deltaSum: 0,
        deltaCount: 0,
        trackChanges: 0,
        hourly: Array.from({ length: 24 }, () => ({ movements: 0, onTime: 0, delaySum: 0 })),
        segments: new Map(),
        trains: [],
      });
    }
    return acc.get(number);
  };

  for (const train of trains) {
    if (!matchesFilters(train, filters)) continue;
    const corridor = corridors.get(train.corridorId);
    if (!corridor) continue;
    if (!touchesStation(train, corridor, filters?.station)) continue;

    const { delays, cancelled, trackChanges } = trainProfile(train, opDay);
    const endDelay = delays[delays.length - 1];

    for (let i = 0; i < train.stops.length; i++) {
      const stop = train.stops[i];
      if (!stop.kbs) continue;
      const entry = ensure(stop.kbs);
      entry.trainIds.add(train.id);
      if (cancelled) { entry.cancelled_count += 1; continue; }

      const delay = delays[i];
      entry.delays.push(delay);
      if (trackChanges[i]) entry.trackChanges += 1;

      const minute = stop.arr ?? stop.dep ?? 0;
      const hour = Math.max(0, Math.min(23, Math.floor(minute / 60) % 24));
      const bucket = entry.hourly[hour];
      bucket.movements += 1;
      bucket.delaySum += delay;
      if (delay < DELAY_THRESHOLD_MIN) bucket.onTime += 1;

      // Delay growth on the section leading to this stop
      if (i > 0) {
        const from = corridor.stations[train.stops[i - 1].s].name;
        const to = corridor.stations[stop.s].name;
        const segKey = `${from} → ${to}`;
        const delta = delays[i] - delays[i - 1];
        const seg = entry.segments.get(segKey) || { key: segKey, sum: 0, count: 0, max: -Infinity };
        seg.sum += delta;
        seg.count += 1;
        seg.max = Math.max(seg.max, delta);
        entry.segments.set(segKey, seg);
        entry.deltaSum += delta;
        entry.deltaCount += 1;
      }
    }

    if (!cancelled) {
      for (const kbsNumber of new Set(train.stops.map(st => st.kbs).filter(Boolean))) {
        ensure(kbsNumber).trains.push({
          trainName: train.trainName_long,
          trainLineName: train.trainLineName,
          terminus: train.trainTerminus,
          // Report semantics: delay at the real terminus, not the worst intermediate value
          delay: endDelay,
        });
      }
    }
  }

  const result = new Map();
  for (const [number, e] of acc) {
    const sorted = [...e.delays].sort((a, b) => a - b);
    const onTime = e.delays.filter(d => d < DELAY_THRESHOLD_MIN).length;
    // Below the report's minimum sample size a verdict would be statistical noise
    const enoughData = e.trainIds.size >= MIN_TRAINS_FOR_VERDICT && e.delays.length > 0;

    result.set(number, {
      kbs_number: number,
      // The mock network is German throughout. The field is emitted all the same, because
      // the client reads it and a mock that leaves out a field the API sends hides exactly
      // the crashes it exists to catch.
      country: 'DE',
      kbs_name: e.kbs_name,
      train_count: e.trainIds.size,
      movement_count: e.delays.length,
      cancelled_count: e.cancelled_count,
      enough_data: enoughData,
      on_time_pct: enoughData ? (onTime / e.delays.length) * 100 : null,
      // NULL, not 0, below the minimum sample - the same contract the API keeps. A mock
      // that fills in 0 here hides every crash that a missing verdict would cause, which
      // is exactly what happened: the traffic-light tooltip read .toFixed() straight off
      // this field and no test ever saw a null.
      delay_avg_min: enoughData ? e.delays.reduce((s, d) => s + d, 0) / e.delays.length : null,
      delay_median_min: enoughData ? sorted[Math.floor(sorted.length / 2)] : null,
      high_delay_count: e.delays.filter(d => d >= HIGH_DELAY_THRESHOLD_MIN).length,
      track_change_count: e.trackChanges,
      delta_avg_min: enoughData && e.deltaCount > 0 ? e.deltaSum / e.deltaCount : null,
      hourly: e.hourly.map((h, hour) => ({
        hour,
        movements: h.movements,
        delay_avg_min: h.movements > 0 ? h.delaySum / h.movements : null,
        on_time_pct: h.movements > 0 ? (h.onTime / h.movements) * 100 : null,
      })),
      segments: Array.from(e.segments.values())
        .filter(s => s.count >= MIN_TRAINS_FOR_VERDICT)
        .map(s => ({
          section: s.key,
          delta_avg_min: s.sum / s.count,
          delta_max_min: s.max,
          count: s.count,
        }))
        .sort((a, b) => b.delta_avg_min - a.delta_avg_min),
      topDelayed: e.trains.sort((a, b) => b.delay - a.delay).slice(0, 5),
    });
  }

  aggCache.set(key, result);
  return result;
}

/** Traffic-light state per KBS for an operating day — mirrors GET /aggregates/kbs?date= */
export async function fetchKbsStatus({ opDay, filters } = {}) {
  const agg = await aggregateDay(opDay, filters);
  const wanted = filters?.kbs;
  return Array.from(agg.values())
    .filter(entry => !wanted || entry.kbs_number === wanted)
    .map(({ hourly, segments, topDelayed, ...rest }) => rest);
}

/** Drill-in for a single KBS — mirrors GET /aggregates/kbs?kbs=&date=&detail=1 */
export async function fetchKbsDetail({ kbsNumber, opDay, filters } = {}) {
  const agg = await aggregateDay(opDay, filters);
  return agg.get(kbsNumber) || null;
}

// ---------------------------------------------------------------------------
// Station statistics — mirrors the per-station block of the daily report
// ---------------------------------------------------------------------------
const stationCache = new Map();

async function aggregateStationDay(stationName, opDay, filters) {
  const key = `${stationName}|${opDay}|${filterSignature(filters)}`;
  if (stationCache.has(key)) return stationCache.get(key);

  const { corridors, stationIndex } = await loadData();
  const stops = stationIndex.get(stationName) || [];

  const buckets = emptyBuckets();
  const messages = new Map();
  const delayed = [];
  let cancelled = 0;

  for (const { train, idx } of stops) {
    if (!matchesFilters(train, filters)) continue;
    const corridor = corridors.get(train.corridorId);
    if (!corridor) continue;

    const profile = trainProfile(train, opDay);
    if (profile.cancelled) { cancelled += 1; continue; }

    const group = trainGroupOf(train);
    const delay = profile.delays[idx];

    for (const target of [group, 'total']) {
      buckets[target].delays.push(delay);
      if (profile.trackChanges[idx]) buckets[target].trackChanges += 1;
    }

    if (profile.message) {
      const entry = messages.get(profile.message.messageText) || {
        ...profile.message, affected_trains: 0, total_mentions: 0,
      };
      entry.affected_trains += 1;
      entry.total_mentions += 1 + (profile.delays[idx] >= DELAY_THRESHOLD_MIN ? 1 : 0);
      messages.set(profile.message.messageText, entry);
    }

    // Report semantics: rank by the delay at the real terminus of the journey
    const endDelay = profile.delays[profile.delays.length - 1];
    if (endDelay > 0) {
      const stop = train.stops[idx];
      delayed.push({
        trainName: train.trainName_long,
        group,
        terminus: train.trainTerminus,
        sched: clockOf(opDay, stop.arr ?? stop.dep),
        act: clockOf(opDay, (stop.arr ?? stop.dep) + delay),
        delay: endDelay,
      });
    }
  }

  const result = {
    stationName,
    opDay,
    groups: summarise(buckets),
    cancelled,
    reasons: Array.from(messages.values())
      .sort((a, b) => b.affected_trains - a.affected_trains || b.total_mentions - a.total_mentions)
      .slice(0, 5),
    topDelayed: delayed.sort((a, b) => b.delay - a.delay).slice(0, 5),
  };
  stationCache.set(key, result);
  return result;
}

/**
 * Full station panel: today's figures, the previous operating day for the pp comparison,
 * and the seven-day punctuality trend (long distance only, as in the report — the traffic
 * mix there stays stable enough to compare across days).
 */
export async function fetchStationStats({ stationName, opDay, filters, trendDays = 7 } = {}) {
  const today = await aggregateStationDay(stationName, opDay, filters);
  const previous = await aggregateStationDay(stationName, shiftOperatingDay(opDay, -1), filters);

  const trend = [];
  for (let i = trendDays - 1; i >= 0; i--) {
    const day = shiftOperatingDay(opDay, -i);
    const stats = i === 0 ? today : await aggregateStationDay(stationName, day, filters);
    trend.push({
      date: day,
      on_time_pct: stats.groups.fern.on_time_pct,
      count: stats.groups.fern.count,
    });
  }

  return { ...today, prevGroups: previous.groups, trend };
}

/** Stations available on the map — carries the UUID the timetable board is keyed on */
export async function fetchStations() {
  const { stations } = await loadData();
  return stations;
}

// ---------------------------------------------------------------------------
// Departure / arrival board — the shape JourneyList renders
// ---------------------------------------------------------------------------
const boardEntry = (train, corridor, stop, idx, profile, opDay, stationUuid) => {
  const station = corridor.stations[stop.s];
  const delay = profile.delays[idx];
  const track = profile.tracks[idx];
  return {
    UUID: train.trainUUID,
    trainUUID: train.trainUUID,
    trainName: train.trainName_long,
    trainName_long: train.trainName_long,
    trainNumber: train.trainNumber,
    trainLineName: train.trainLineName,
    trainTypeProductCategorie: train.trainTypeProductCategorie,
    trainTypeValue: train.trainTypeValue,
    trainTerminus: train.trainTerminus,
    operatingDate: opDay,
    trainSeries: [{
      trainSerieUUID: train.trainSerieUUID,
      trainSerieNumber: train.trainSerieNumber,
      trainSerieName: train.trainSerieName,
      trainSerieOperator: train.trainSerieOperator,
      trainSerieManufacturer: train.trainSerieManufacturer,
      trainSerieExpected: 1,
    }],
    journey: {
      journeyUUID: train.journeyUUID,
      journeyCancelled: profile.cancelled ? 1 : 0,
      journeyAttributes: profile.attributes.map(key => ({ categorieKey: key })),
    },
    stationRequest: {
      stationUUID: stationUuid,
      stationName: station.name,
      arrivalTime_scheduled: minutesToLocalISO(opDay, stop.arr),
      arrivalTime_actual: minutesToLocalISO(opDay, stop.arr === null ? null : stop.arr + delay),
      departureTime_scheduled: minutesToLocalISO(opDay, stop.dep),
      departureTime_actual: minutesToLocalISO(opDay, stop.dep === null ? null : stop.dep + delay),
      track_scheduled: track.scheduled,
      track_actual: track.actual,
      messages: profile.message
        ? [{
          messageText: profile.message.messageText,
          priority: profile.message.priority === 'HOCH' ? 2 : 1,
        }]
        : [],
    },
  };
};

/**
 * Arrivals or departures at one station — mirrors GET /v1/stations/{uuid}/timetable.
 * Anchored on `at` so the board always shows the same moment the map does.
 */
export async function fetchStationBoard({ stationUUID, at, type = 'departure', limit = 50, offset = 0 } = {}) {
  const { corridors, stationIndex, stationsByUuid } = await loadData();
  const station = stationsByUuid.get(stationUUID);
  if (!station) return [];

  const when = at instanceof Date ? at : new Date(at || Date.now());
  const opDay = toOperatingDay(when);
  const fromMinute = minutesOfDay(when, opDay) - 30; // a little history, as the real board has

  const stops = stationIndex.get(station.name) || [];
  const rows = [];
  for (const { train, idx } of stops) {
    const corridor = corridors.get(train.corridorId);
    if (!corridor) continue;
    const stop = train.stops[idx];
    const minute = type === 'arrival' ? stop.arr : stop.dep;
    if (minute === null || minute === undefined) continue;
    if (minute < fromMinute) continue;

    const profile = trainProfile(train, opDay);
    rows.push({ minute, entry: boardEntry(train, corridor, stop, idx, profile, opDay, stationUUID) });
  }

  return rows
    .sort((a, b) => a.minute - b.minute)
    .slice(offset, offset + limit)
    .map(r => r.entry);
}

/** Train search — mirrors GET /v1/trains */
export async function searchTrains({
  query, operatingDate, trainLine, trainSerie, trainNumber, trainTypeProductCategorie, limit = 60,
} = {}) {
  const { trains } = await loadData();
  const opDay = operatingDate || toOperatingDay(new Date());
  const needle = (query || '').trim().toLowerCase();

  const hits = [];
  for (const train of trains) {
    if (needle) {
      const haystack = `${train.trainName_long} ${train.trainLineName} ${train.trainNumber}`.toLowerCase();
      if (!haystack.includes(needle)) continue;
    }
    if (trainLine && train.trainLineName !== trainLine) continue;
    if (trainSerie && train.trainSerieNumber !== trainSerie) continue;
    if (trainNumber && !String(train.trainNumber).includes(String(trainNumber))) continue;
    if (trainTypeProductCategorie && train.trainTypeProductCategorie !== trainTypeProductCategorie) continue;

    hits.push({
      UUID: train.trainUUID,
      trainUUID: train.trainUUID,
      journeyUUID: train.journeyUUID,
      trainName_long: train.trainName_long,
      trainName: train.trainName_long,
      trainNumber: train.trainNumber,
      trainLineName: train.trainLineName,
      trainTypeProductCategorie: train.trainTypeProductCategorie,
      trainTypeValue: train.trainTypeValue,
      trainTerminus: train.trainTerminus,
      operatingDate: opDay,
      trainSeries: [{
        trainSerieUUID: train.trainSerieUUID,
        trainSerieNumber: train.trainSerieNumber,
        trainSerieName: train.trainSerieName,
        trainSerieOperator: train.trainSerieOperator,
        trainSerieExpected: 1,
      }],
      // first departure, so the map knows where in the day this run lives
      _startMinute: train.stops[0].dep,
      _endMinute: train.stops[train.stops.length - 1].arr,
    });
    if (hits.length >= limit) break;
  }
  return hits;
}

// ---------------------------------------------------------------------------
// Network-wide operating-day statistics — the daily report, but for the whole network
// and for the operating day the map is showing.
//
// Note the deliberate difference to the station panel: per station the report counts every
// movement, for network totals it deduplicates per train and takes its LAST observation.
// A train that arrived 12 min late but caught up by its terminus counts as punctual here.
// ---------------------------------------------------------------------------
const networkCache = new Map();

async function aggregateNetworkDay(opDay, filters) {
  const key = `net|${opDay}|${filterSignature(filters)}`;
  if (networkCache.has(key)) return networkCache.get(key);

  const { corridors, trains } = await loadData();
  const buckets = emptyBuckets();
  const messages = new Map();
  const delayed = [];
  let cancelled = 0;

  for (const train of trains) {
    if (!matchesFilters(train, filters)) continue;
    const corridor = corridors.get(train.corridorId);
    if (!corridor) continue;
    if (!touchesStation(train, corridor, filters?.station)) continue;

    const profile = trainProfile(train, opDay);
    if (profile.cancelled) { cancelled += 1; continue; }

    const group = trainGroupOf(train);
    const endDelay = profile.delays[profile.delays.length - 1];
    const trackChanged = profile.trackChanges.some(Boolean);

    for (const target of [group, 'total']) {
      buckets[target].delays.push(endDelay);
      if (trackChanged) buckets[target].trackChanges += 1;
    }

    if (profile.message) {
      const entry = messages.get(profile.message.messageText) || {
        ...profile.message, affected_trains: 0, total_mentions: 0,
      };
      entry.affected_trains += 1;
      entry.total_mentions += 1;
      messages.set(profile.message.messageText, entry);
    }

    if (endDelay > 0) {
      const lastStop = train.stops[train.stops.length - 1];
      delayed.push({
        trainName: train.trainName_long,
        trainLineName: train.trainLineName,
        group,
        terminus: train.trainTerminus,
        sched: clockOf(opDay, lastStop.arr),
        act: clockOf(opDay, lastStop.arr + endDelay),
        delay: endDelay,
      });
    }
  }

  const result = {
    opDay,
    groups: summarise(buckets),
    cancelled,
    topDelayed: delayed.sort((a, b) => b.delay - a.delay).slice(0, 10),
    reasons: Array.from(messages.values())
      .sort((a, b) => b.affected_trains - a.affected_trains)
      .slice(0, 8),
  };
  networkCache.set(key, result);
  return result;
}

// Punctuality trend is long-distance only — the report's reasoning: the traffic mix stays
// stable there, while a shifting regional share would distort the comparison across days.
const TREND_GROUP = 'fern';

// Monthly values sample a few days instead of all ~30. Every sampled day costs a pass over
// the journeys; the real backend reads this from daily_aggregates_* in a single query.
const MONTH_SAMPLE_DAYS = [5, 13, 21, 28];

// Cheap trend pass: only the group the trend is about, and only the delay at the terminus.
// The full aggregation walks every group and builds lists — far too much for 30 days.
const trendCache = new Map();

async function trendPoint(opDay, filters, group) {
  const key = `trend|${group}|${opDay}|${filterSignature(filters)}`;
  if (trendCache.has(key)) return trendCache.get(key);

  const { corridors, trains } = await loadData();
  let count = 0;
  let onTime = 0;
  for (const train of trains) {
    if (trainGroupOf(train) !== group) continue;
    if (!matchesFilters(train, filters)) continue;
    const corridor = corridors.get(train.corridorId);
    if (!corridor) continue;
    if (!touchesStation(train, corridor, filters?.station)) continue;

    const profile = trainProfile(train, opDay);
    if (profile.cancelled) continue;
    count += 1;
    if (profile.delays[profile.delays.length - 1] < DELAY_THRESHOLD_MIN) onTime += 1;
  }

  const point = { on_time_pct: count > 0 ? (onTime / count) * 100 : null, count };
  trendCache.set(key, point);
  return point;
}

// Hand the main thread back between passes so the dashboard can paint its spinner instead
// of freezing the map while a month is being walked
const yieldToUi = () => new Promise(resolve => setTimeout(resolve, 0));

export async function fetchNetworkStats({
  opDay, filters, trendDays = 7, trendMonths = 6,
} = {}) {
  const today = await aggregateNetworkDay(opDay, filters);
  const previous = await aggregateNetworkDay(shiftOperatingDay(opDay, -1), filters);

  const daily = [];
  for (let i = trendDays - 1; i >= 0; i--) {
    const day = shiftOperatingDay(opDay, -i);
    const point = i === 0
      ? {
        on_time_pct: today.groups[TREND_GROUP].on_time_pct,
        count: today.groups[TREND_GROUP].count,
      }
      : await trendPoint(day, filters, TREND_GROUP);
    daily.push({ date: day, ...point });
  }

  const [year, month] = opDay.split('-').map(Number);
  const monthly = [];
  for (let back = trendMonths - 1; back >= 0; back--) {
    let y = year;
    let m = month - back;
    while (m <= 0) { m += 12; y -= 1; }

    let sum = 0;
    let weight = 0;
    for (const day of MONTH_SAMPLE_DAYS) {
      const point = await trendPoint(`${y}-${pad(m)}-${pad(day)}`, filters, TREND_GROUP);
      if (point.on_time_pct === null) continue;
      sum += point.on_time_pct * point.count;
      weight += point.count;
    }
    monthly.push({
      year: y,
      month: m,
      on_time_pct: weight > 0 ? sum / weight : null,
      count: weight,
      sampled: true,
    });
    await yieldToUi();
  }

  // Worst routes and sections come from the KBS aggregation that already exists
  const kbsAgg = await aggregateDay(opDay, filters);
  const kbsEntries = Array.from(kbsAgg.values()).filter(k => k.enough_data);
  const worstKbs = [...kbsEntries]
    .sort((a, b) => a.on_time_pct - b.on_time_pct || b.delay_avg_min - a.delay_avg_min)
    .slice(0, 5);
  const worstSegments = kbsEntries
    .flatMap(k => k.segments.map(s => ({ ...s, kbs_number: k.kbs_number })))
    .sort((a, b) => b.delta_avg_min - a.delta_avg_min)
    .slice(0, 5);

  return {
    ...today,
    prevGroups: previous.groups,
    prevCancelled: previous.cancelled,
    trend: { daily, monthly },
    worstKbs,
    worstSegments,
  };
}

/** KBS geometry — real OSM data when fetched, placeholder otherwise */
export async function fetchKbsGeometry() {
  const { kbs, osmKbs } = await loadData();
  return osmKbs && osmKbs.length > 0 ? osmKbs : kbs;
}

/**
 * Full run of one train: every stop with times and delay, plus the route geometry.
 * Real backend: GET /v1/trains/{uuid} for the stops, /v1/journeys/{uuid}/locations-json
 * for the polyline — both already exist and are what the train detail view uses.
 */
/**
 * Delay history of a line, built from the same deterministic day model the map uses, so the
 * history agrees with what the map showed on those days instead of being separate noise.
 */
export async function fetchTrainHistory(train, days = 90) {
  // A real request takes a round trip; the mock answered within the same microtask queue,
  // before React had rendered again. That hid a panel that threw away every real answer —
  // the history stayed on "wird geladen" against the backend and worked against the mock.
  await new Promise(resolve => setTimeout(resolve, 250));
  const { corridors, trains } = await loadData();
  const line = train?.trainLineName || train?.trainName;
  const today = toOperatingDay(new Date());
  const perDay = new Map();

  const matching = trains.filter(t => (t.trainLineName || t.trainName_long) === line);
  for (let back = days - 1; back >= 0; back--) {
    const d = new Date(`${today}T12:00:00`);
    d.setDate(d.getDate() - back);
    const opDay = d.toISOString().slice(0, 10);
    let worst = null;
    for (const t of matching) {
      if (!corridors.get(t.corridorId)) continue;
      const { delays, cancelled } = trainProfile(t, opDay);
      if (cancelled || !delays.length) continue;
      const end = delays[delays.length - 1];
      if (worst === null || end > worst) worst = end;
    }
    if (worst !== null) perDay.set(opDay, worst);
  }

  const summarise = (vals) => (vals.length
    ? {
      runs: vals.length,
      delay_avg_min: Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10,
      on_time_pct: Math.round((100 * vals.filter(v => v < DELAY_THRESHOLD_MIN).length
        / vals.length) * 10) / 10,
    }
    : { runs: 0, delay_avg_min: null, on_time_pct: null });

  const dates = [...perDay.keys()].sort();
  const valuesOf = (ds) => ds.map(d => perDay.get(d));
  const monthCut = dates.filter(d => {
    const age = (new Date(`${today}T12:00:00`) - new Date(`${d}T12:00:00`)) / 86400000;
    return age < 30;
  });

  return {
    line,
    from: dates[0] || today,
    to: dates[dates.length - 1] || today,
    days: dates.map(d => ({ date: d, delay_min: perDay.get(d) })),
    recent: summarise(valuesOf(dates.slice(-14))),
    month: summarise(valuesOf(monthCut)),
    year: summarise(valuesOf(dates)),
    weekday: Array.from({ length: 7 }, (_, wd) => ({
      weekday: wd,
      ...summarise(valuesOf(dates.filter(d => (new Date(`${d}T12:00:00`).getDay() + 6) % 7 === wd))),
    })),
  };
}

export async function fetchTrainJourney(train) {
  const { corridors, trainsByUuid } = await loadData();
  const mockTrain = trainsByUuid.get(train.trainUUID || train.globalTrainID);
  const corridor = mockTrain && corridors.get(mockTrain.corridorId);
  if (!mockTrain || !corridor) return null;

  const opDay = train.operatingDate || toOperatingDay(new Date());
  const profile = trainProfile(mockTrain, opDay);
  // The real API reports when the import last wrote the run; the mock pretends it was an
  // hour ago, so the panel's freshness line has something to show here too
  const collectedAt = new Date(Date.now() - 3600 * 1000).toISOString().slice(0, 19);
  const { delays, tracks, capacity, cancelled, message, attributes } = profile;

  const stations = buildJourneyStations(mockTrain, corridor, opDay, delays)
    .map((s, i) => ({
      ...s,
      delay: delays[i],
      track_scheduled: tracks[i].scheduled,
      track_actual: tracks[i].actual,
      capacityLevelClass1: capacity[i].c1,
      capacityLevelClass2: capacity[i].c2,
    }));

  return {
    collectedAt,
    trainName: mockTrain.trainName_long,
    trainNumber: mockTrain.trainNumber,
    trainLineName: mockTrain.trainLineName,
    trainTypeProductCategorie: mockTrain.trainTypeProductCategorie,
    trainTypeValue: mockTrain.trainTypeValue,
    operatingDate: opDay,
    cancelled,
    trainSeries: [{
      trainSerieUUID: mockTrain.trainSerieUUID,
      trainSerieNumber: mockTrain.trainSerieNumber,
      trainSerieName: mockTrain.trainSerieName,
      trainSerieOperator: mockTrain.trainSerieOperator,
      trainSerieManufacturer: mockTrain.trainSerieManufacturer,
      trainSerieExpected: 1,
    }],
    attributes: attributes.map(key => ({ categorieKey: key })),
    messages: message
      ? [{ messageText: message.messageText, priority: message.priority === 'HOCH' ? 2 : 1 }]
      : [],
    stations,
    coords: stations.map(s => [s.lat, s.lng]),
  };
}

/** Filter dropdown contents — real backend: /v1/train-lines, /v1/train-series, /v1/stations */
export async function fetchFilterOptions() {
  const { trains, stations, kbs, osmKbs } = await loadData();
  const lines = new Set();
  const series = new Map();

  for (const train of trains) {
    lines.add(train.trainLineName);
    if (!series.has(train.trainSerieNumber)) {
      series.set(train.trainSerieNumber, train.trainSerieName);
    }
  }

  const collator = new Intl.Collator('de', { numeric: true });
  const kbsList = osmKbs && osmKbs.length > 0 ? osmKbs : kbs;
  return {
    lines: Array.from(lines).sort(collator.compare).map(l => ({ value: l, label: l })),
    series: Array.from(series.entries())
      .sort((a, b) => collator.compare(a[0], b[0]))
      .map(([number, name]) => ({ value: number, label: `${number} — ${name}` })),
    stations: stations.map(s => s.name).sort(collator.compare).map(s => ({ value: s, label: s })),
    kbs: kbsList.map(k => ({ value: k.kbs_number, label: `${k.kbs_number} — ${k.kbs_name}` })),
  };
}
